/**
 * Return-path and action-URL validation (APP-001 §8, §12 invariant 11).
 *
 * The frontend must never perform an open redirect to an external domain, and
 * server-provided action URLs are validated before navigation. Return paths
 * preserved through sign-in (`?redirect=/playground`) must be same-origin
 * application paths only.
 */
import { APPROVED_EXTERNAL_HOSTS } from '@/config/links';

/**
 * A safe return path: absolute path on the current origin, rooted at `/`, no
 * protocol/host. Returns `null` for anything external or malformed.
 */
export function sanitizeReturnPath(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;
  // Allow the bare origin only.
  if (raw === '/') return '/';

  // Must start with a single slash (no protocol-relative //evil.com).
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  // Backslash-based protocol-relative bypass.
  if (raw.startsWith('/\\')) return null;

  try {
    // Parse as a URL relative to a placeholder origin; reject anything that
    // resolves to an absolute URL with a foreign host.
    const resolved = new URL(raw, 'https://app.local');
    if (resolved.host !== 'app.local') return null;
    const path = `${resolved.pathname}${resolved.search}${resolved.hash}`;
    // Reject javascript:/data: schemes smuggled via search/hash by re-parsing.
    if (/(javascript|data|vbscript):/i.test(path)) return null;
    // Cap length to avoid abuse.
    if (path.length > 1024) return null;
    return path;
  } catch {
    return null;
  }
}

/**
 * Validate an external action URL returned by the backend before navigation.
 * Only same-origin app routes or approved first-party/Stripe-hosted hosts pass.
 */
export function isApprovedExternalUrl(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:') return false;
  if (url.username || url.password) return false;
  return APPROVED_EXTERNAL_HOSTS.has(url.host);
}

/**
 * Resolve a server action URL to a safe navigation target. Returns `null` if
 * the URL is neither a same-origin path nor an approved external URL.
 */
export function resolveActionTarget(raw: string | null | undefined): string | null {
  if (!raw) return null;
  // Same-origin absolute path.
  const local = sanitizeReturnPath(raw);
  if (local) return local;
  if (isApprovedExternalUrl(raw)) return raw;
  return null;
}
